import { useState } from "react";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { ClaimSource } from "Types";
import { useConfigurationQuery } from "../services/sso.api";
import { OIDCFormData, Protocol, SAMLFormData } from "../types";
import {
  useLocalConfigForm,
  useOidcSSOConfigForm,
  useSamlSSOConfigForm,
} from "./index";

type BaseSSOConfiguration = {
  onSubmit: () => void;
  protocolForm: UseFormReturn<{ protocol: Protocol }>;
  isSaveDisabled: boolean;
  isConfirmSaveOpen: boolean;
  openConfirmSaveDialog: () => void;
  closeConfirmSaveDialog: () => void;
  handleConfirm: () => void;
  isLoading: boolean;
};

type LocalConfiguration = {
  protocol: "local";
  errors: null;
  methods: null;
  claimSource: null;
};

type SamlConfiguration = {
  protocol: "sso-saml";
  errors: FieldErrors<SAMLFormData>;
  methods: UseFormReturn<SAMLFormData>;
  claimSource: null;
};

type OidcConfiguration = {
  protocol: "sso-oidc";
  errors: FieldErrors<OIDCFormData>;
  methods: UseFormReturn<OIDCFormData>;
  claimSource: ClaimSource;
};

type UseSSOConfiguration = BaseSSOConfiguration &
  (LocalConfiguration | SamlConfiguration | OidcConfiguration);

const SSOConfiguration = (
  protocolForm: UseFormReturn<{ protocol: Protocol }>,
): UseSSOConfiguration => {
  const currentOrganization = useCurrentOrganization();
  const [isUpdating, setIsUpdating] = useState(false);

  const isAdmin = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });

  const { data: ssoConfiguration, isLoading: isSsoConfigLoading } =
    useConfigurationQuery(
      {
        orgId: currentOrganization?.id || "",
      },
      { skip: isUpdating || !currentOrganization || !isAdmin },
    );

  const oidcForm = useOidcSSOConfigForm(
    ssoConfiguration?.user_directory.sso_oidc_configuration_id,
  );
  const samlForm = useSamlSSOConfigForm(
    ssoConfiguration?.user_directory.sso_saml_configuration_id,
  );
  const localForm = useLocalConfigForm();
  const selectedProtocol = protocolForm.watch("protocol");

  const handleConfirm = () => {
    setIsUpdating(true);
    if (selectedProtocol === "local") {
      localForm.closeConfirmSaveDialog();
      return localForm.handleDisableSSO();
    } else if (selectedProtocol === "sso-oidc") {
      oidcForm.closeConfirmSaveDialog();
      return oidcForm.handleUpdateSSOConfiguration();
    } else if (selectedProtocol === "sso-saml") {
      samlForm.closeConfirmSaveDialog();
      return samlForm.handleUpdateSSOConfiguration();
    }
  };

  const isSaveDisabled = () => {
    if (selectedProtocol !== ssoConfiguration?.user_directory.active) {
      return false;
    }
    if (selectedProtocol === "local") {
      return true;
    } else if (selectedProtocol === "sso-oidc") {
      return !oidcForm.isDirty;
    } else if (selectedProtocol === "sso-saml") {
      return !samlForm.isDirty;
    }
    return true;
  };

  if (selectedProtocol === "sso-oidc") {
    return {
      protocol: "sso-oidc",
      errors: oidcForm.errors,
      methods: oidcForm.methods,
      claimSource: oidcForm.claimSource,
      onSubmit: oidcForm.onSubmit,
      isSaveDisabled: isSaveDisabled(),
      isConfirmSaveOpen: oidcForm.isConfirmSaveOpen,
      openConfirmSaveDialog: oidcForm.openConfirmSaveDialog,
      closeConfirmSaveDialog: oidcForm.closeConfirmSaveDialog,
      protocolForm,
      handleConfirm,
      isLoading: isSsoConfigLoading || oidcForm.isLoading,
    };
  } else if (selectedProtocol === "sso-saml") {
    return {
      protocol: "sso-saml",
      errors: samlForm.errors,
      methods: samlForm.methods,
      claimSource: null,
      onSubmit: samlForm.onSubmit,
      isSaveDisabled: isSaveDisabled(),
      isConfirmSaveOpen: samlForm.isConfirmSaveOpen,
      openConfirmSaveDialog: samlForm.openConfirmSaveDialog,
      closeConfirmSaveDialog: samlForm.closeConfirmSaveDialog,
      protocolForm,
      handleConfirm,
      isLoading: isSsoConfigLoading || samlForm.isLoading,
    };
  } else {
    return {
      protocol: "local",
      errors: null,
      methods: null,
      claimSource: null,
      onSubmit: localForm.onSubmit,
      isSaveDisabled: isSaveDisabled(),
      isConfirmSaveOpen: localForm.isConfirmSaveOpen,
      openConfirmSaveDialog: localForm.openConfirmSaveDialog,
      closeConfirmSaveDialog: localForm.closeConfirmSaveDialog,
      protocolForm,
      handleConfirm,
      isLoading: isSsoConfigLoading,
    };
  }
};

export default SSOConfiguration;
